import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';

import { ButtonInfo } from 'components/buttons';
import Tooltip from 'components/Tooltip';
import { t } from 'utils/getLiteral';
import { numbers, decimals } from 'utils/normalizations';
import CrudCheckbox from './CrudCheckbox';
import CrudInputText from './CrudInputText';
import CrudPassword from './CrudPassword';
import CrudTextArea from './CrudTextArea';
import CrudDates from './CrudDates';

import CrudBoolSelect from './CrudBoolSelect';
import CrudFuzzy from './CrudFuzzy';
import CrudMultiSelect from './CrudMultiSelect';
import CrudMultiSelectNew from './CrudMultiSelectNew';
import CrudSelect from './CrudSelect';
import CrudSwitch from './CrudSwitch';
import CrudUploadPhoto from './CrudUploadPhoto';
import CrudUploadFiles from './CrudUploadFiles';
import CrudWidgetSize from './CrudWidgetSize';
import CrudInfo from './CrudInfo';

import './styles.scss';

class CrudField extends PureComponent {
    constructor(props) {
        super(props);
        this.fieldRef = React.createRef();
    }

    focus = () => {
        if (this.fieldRef && this.fieldRef.current) {
            this.fieldRef.current.focus();
        }
    };

    renderLabel() {
        const { infoLabel, isMultilang = true, label = '', required } = this.props;
        const lbl = isMultilang ? t(label) : label;

        if (label.length > 0) {
            return (
                <div className="col-form-label">
                    <span>{`${lbl} ${required ? '*' : ''}`}</span>
                    {infoLabel && (
                        <Tooltip
                            title={t(infoLabel)}
                            position="top"
                            containerClassName="crud-lbl-tooltip"
                        >
                            <ButtonInfo />
                        </Tooltip>
                    )}
                </div>
            );
        }
        return <div className="col-sm-4" />;
    }

    renderField() {
        const {
            type,
            name = '',
            options = [],
            checks = [],
            readOnly,
            copiable,
            onBlur = () => null,
            onChange = () => null,
            onError = () => null,
            onFocus = () => null,
            list = '',
            isClearable = true,
            validate = [],
            emptyValue = '',
            multipleUpload = true,
            fileType = '*/*',
            filesLimit,
            dataTest = '',
            linkAction,
            filterOption,
            isDismissible,
            autoFocus,
            text,
            showCollapse,
        } = this.props;

        const componentProps = {
            dataTest: dataTest || name,
            autoFocus,
        };

        let { placeholder } = this.props;
        const ref = this.fieldRef;

        switch (type) {
            case 'file':
                return (
                    <Field
                        key={name}
                        validate={validate}
                        name={name}
                        component={CrudUploadPhoto}
                        props={{ ...componentProps, onError }}
                    />
                );

            case 'photos':
            case 'zip':
                return (
                    <Field
                        key={name}
                        validate={validate}
                        name={name}
                        component={CrudUploadFiles}
                        multipleUpload={multipleUpload}
                        fileType={fileType}
                        filesLimit={filesLimit}
                        props={{ ...componentProps, onError }}
                    />
                );

            case 'checkbox':
                return (
                    <Field
                        key={name}
                        name={name}
                        onChange={onChange}
                        validate={validate}
                        component={CrudCheckbox}
                        props={componentProps}
                        readOnly={readOnly}
                    />
                );

            case 'multicheckbox':
                return checks.map((check) => (
                    <Field
                        key={check.name}
                        name={check.name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            label: check.label,
                        }}
                        component={CrudCheckbox}
                    />
                ));
            case 'date':
            case 'datetime':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                        }}
                        component={CrudDates}
                    />
                );
            case 'info':
                return (
                    <Field
                        name={name}
                        text={text}
                        showCollapse={showCollapse}
                        component={CrudInfo}
                    />
                );

            case 'select':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            options,
                            list,
                            onChange,
                            readOnly,
                            placeholder,
                            isClearable,
                            emptyValue,
                            linkAction,
                            filterOption,
                        }}
                        component={CrudSelect}
                    />
                );

            case 'multiselect':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            placeholder,
                            readOnly,
                            onBlur,
                            list,
                            options,
                            filterOption,
                            isDismissible,
                        }}
                        component={CrudMultiSelect}
                    />
                );

            case 'multiselectnew':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            placeholder,
                            readOnly,
                            onBlur,
                            list,
                            options,
                            filterOption,
                            isDismissible,
                        }}
                        component={CrudMultiSelectNew}
                    />
                );
            case 'boolselect':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            onChange,
                            readOnly,
                            placeholder,
                            isClearable,
                        }}
                        component={CrudBoolSelect}
                    />
                );

            case 'switch':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        component={CrudSwitch}
                        props={{
                            ...componentProps,
                            readOnly,
                        }}
                    />
                );

            case 'fuzzy':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        props={{
                            ...componentProps,
                            onChange,
                            readOnly,
                            placeholder,
                            list,
                            filterOption,
                        }}
                        component={CrudFuzzy}
                    />
                );

            case 'password':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        component={CrudPassword}
                        props={{
                            ...componentProps,
                            placeholder: placeholder || 'cfm_placeholder_type_value',
                            readOnly,
                            onBlur,
                            onFocus,
                        }}
                    />
                );
            case 'text':
            case 'number':
            case 'decimal': {
                let normalize = null;
                if (type === 'number') {
                    normalize = numbers;
                    placeholder = placeholder || 'cfm_placeholder_integer';
                } else if (type === 'decimal') {
                    normalize = decimals;
                    placeholder = placeholder || 'cfm_placeholder_decimal';
                } else if (type === 'text') {
                    placeholder = placeholder || 'cfm_placeholder_type_value';
                }

                return (
                    <Field
                        name={name}
                        validate={validate}
                        component={CrudInputText}
                        props={{
                            ...componentProps,
                            placeholder,
                            readOnly,
                            copiable,
                            onBlur,
                            onFocus,
                            ref,
                        }}
                        normalize={normalize}
                    />
                );
            }

            case 'textarea':
                return (
                    <Field
                        name={name}
                        validate={validate}
                        component={CrudTextArea}
                        props={{
                            ...componentProps,
                            placeholder: placeholder || 'cfm_placeholder_type_value',
                            readOnly,
                            onBlur,
                            onFocus,
                            isClearable,
                            ref,
                        }}
                    />
                );

            case 'widget-size':
                return (
                    <Field
                        name={name}
                        component={CrudWidgetSize}
                        props={{
                            ...componentProps,
                            onChange,
                        }}
                    />
                );

            default:
                return '';
        }
    }

    render() {
        const {
            className = '',
            isFullWidth,
            label = '',
            labelMode = 'horizontal',
            name = '',
        } = this.props;
        const labelVisible = labelMode !== 'none';

        return (
            <div data-test={label} className={`form-group ${className}`}>
                {labelVisible && this.renderLabel()}
                <div
                    className={`col-form-element col-form-element-${name} ${
                        isFullWidth ? 'full-width' : ''
                    }`}
                >
                    {this.renderField()}
                </div>
            </div>
        );
    }
}

CrudField.propTypes = {
    label: PropTypes.string,
    infoLabel: PropTypes.string,
    required: PropTypes.bool,
    className: PropTypes.string,
    type: PropTypes.string.isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    checks: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }),
    ),
    readOnly: PropTypes.bool,
    copiable: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onError: PropTypes.func,
    onFocus: PropTypes.func,
    list: PropTypes.string,
    isClearable: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    isMultilang: PropTypes.bool,
    validate: PropTypes.arrayOf(PropTypes.func),
    emptyValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
    labelMode: PropTypes.string,
    multipleUpload: PropTypes.bool,
    fileType: PropTypes.string,
    filesLimit: PropTypes.number,
    dataTest: PropTypes.string,
    linkAction: PropTypes.shape({
        text: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
    }),
};

export default CrudField;
